import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircleUserRound,
  Users,
  BarChart2,
  Video,
  CreditCard,
  Plug,
  Blocks,
  Bot,
  History,
} from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import AccountSettings from './profile/Account';
import TeamSettings from './Team';
import Usage from './Usage';
import RecordingSettings from './Recorder';
import Billing from './billing/Billing';
import PluginSettings from './API';
import Integrations from './integrations';
import MeetingBotSettings from './meetbot';
import RecoverySettings from './Recover';
import { useRole } from './hooks';
import { SettingsKeys } from './settingsSlice';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: SettingsKeys;
}

interface SettingsTab {
  type: SettingsKeys;
  label: string;
  component: React.ReactNode;
  icon: React.ReactElement;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  onClose,
  initialTab = 'user',
}) => {
  const { t } = useTranslation();
  const { isRole } = useRole();
  const [activeTab, setActiveTab] = useState<SettingsKeys>(initialTab);

  React.useEffect(() => {
    if (open && initialTab) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  const allSettingsTabs = useMemo<SettingsTab[]>(
    () => [
      {
        type: 'user',
        label: t('settings.account', 'Account'),
        component: <AccountSettings />,
        icon: <CircleUserRound className="w-4 h-4" />,
      },
      {
        type: 'team',
        label: t('settings.team', 'Team'),
        component: <TeamSettings />,
        icon: <Users className="w-4 h-4" />,
      },
      {
        type: 'usage',
        label: t('settings.usage', 'Usage'),
        component: <Usage />,
        icon: <BarChart2 className="w-4 h-4" />,
      },
      {
        type: 'recorder',
        label: t('settings.recording', 'Recording'),
        component: <RecordingSettings />,
        icon: <Video className="w-4 h-4" />,
      },
      {
        type: 'billing',
        label: t('settings.billing.drawerTitle', 'Billing'),
        component: <Billing onClose={onClose} />,
        icon: <CreditCard className="w-4 h-4" />,
      },
      {
        type: 'plugin',
        label: t('settings.api.main.title', 'API'),
        component: <PluginSettings />,
        icon: <Plug className="w-4 h-4" />,
      },
      {
        type: 'integrations',
        label: t('settings.integrations.title', 'Integrations'),
        component: <Integrations />,
        icon: <Blocks className="w-4 h-4" />,
      },
      {
        type: 'meetingbot',
        label: t('settings.meetingBot.title', 'Meeting Bot'),
        component: <MeetingBotSettings />,
        icon: <Bot className="w-4 h-4" />,
      },
      {
        type: 'recovery',
        label: t('settings.recovery', 'Recovery'),
        component: <RecoverySettings />,
        icon: <History className="w-4 h-4" />,
      },
    ],
    [t],
  );

  const canAccessTab = (type: SettingsKeys) => {
    if (type === 'billing') {
      return isRole(['Owner']);
    }
    if (type === 'team' || type === 'integrations') {
      return isRole(['Owner', 'Admin']);
    }
    return true;
  };

  const accessibleTabs = allSettingsTabs.filter((tab) =>
    canAccessTab(tab.type),
  );

  const currentTabLabel =
    accessibleTabs.find((tab) => tab.type === activeTab)?.label || '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[95vw] md:max-w-[85vw] lg:max-w-[75vw] xl:max-w-[70vw] w-full sm:w-[640px] md:w-[768px] lg:w-[900px] xl:w-[1024px] h-[90vh] sm:h-[85vh] md:h-[85vh] lg:h-[80vh] xl:h-[80vh] pt-10 sm:pt-0 p-0 overflow-hidden !flex !flex-row !gap-0">
        <Tabs
          value={activeTab}
          onValueChange={(value: string) => setActiveTab(value as SettingsKeys)}
          className="flex flex-col sm:flex-row h-full min-h-0 w-full"
        >
          <div className="w-full sm:w-32 md:w-36 lg:w-40 xl:w-44 flex-shrink-0 border-b sm:border-b-0 sm:border-r flex flex-col bg-gray-50/50 dark:bg-muted/20">
            <div className="sm:hidden flex items-center justify-center py-3 px-4 border-b">
              <h2 className="text-lg font-semibold">{currentTabLabel}</h2>
            </div>
            <div className="flex-1 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto p-2 sm:py-4">
              <TabsList className="flex flex-row sm:flex-col h-auto bg-transparent p-0 gap-1 sm:gap-0.5 sm:px-2 min-w-max sm:min-w-0">
                {accessibleTabs.map(({ type, label, icon }) => (
                  <TabsTrigger
                    key={type}
                    value={type}
                    className="flex-shrink-0 justify-center sm:justify-start sm:w-full gap-1.5 sm:gap-2 px-3 py-2.5 sm:px-2.5 sm:py-2 rounded-lg data-[state=active]:bg-white/80 dark:data-[state=active]:bg-muted/50 data-[state=active]:shadow-sm hover:bg-white/40 dark:hover:bg-muted/30 hover:text-accent-foreground transition-all whitespace-nowrap"
                  >
                    <span className="flex items-center gap-1.5">
                      {icon}
                      <span className="text-xs md:text-sm font-medium">
                        {label}
                      </span>
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-background min-h-0">
            <div className="hidden sm:flex items-center justify-center py-3 px-4 border-b">
              <h2 className="text-lg font-semibold">{currentTabLabel}</h2>
            </div>
            {accessibleTabs.map(({ type, component }) => (
              <TabsContent
                key={type}
                value={type}
                className="flex-1 min-h-0 m-0 overflow-hidden h-full"
              >
                {component}
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
